/**
 * Sign in (CLAUDE.md 11.6, 11.7).
 *
 * The strings have existed in the shared `auth` i18n namespace since Phase A0; there
 * was simply no page rendering them, so the application was reachable only as an
 * anonymous demo calculator.
 *
 * Identifier is email *or* username, because engineers do not reliably remember which
 * they registered with, and the backend accepts either.
 */
import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common'])
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      await login(identifier, password)
      // Back where they were headed. Two ways in, one destination: the route guard
      // puts the path in location state, and a page that linked here deliberately --
      // an invitation, say -- passes `?next=`. A second, parallel mechanism would be
      // one more thing to forget.
      //
      // Only a same-site path is honoured. `next=https://elsewhere` would turn the
      // sign-in form into an open redirect, which is a phishing primitive: the link
      // genuinely starts on our domain, so the user has no way to tell.
      const requested = searchParams.get('next')
      const next = requested && /^\/(?![/\\])/.test(requested) ? requested : null
      const from = (location.state as { from?: string } | null)?.from
      navigate(next ?? from ?? '/', { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.localised : t('error.auth.invalid_credentials'))
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthLayout title={t('login.title')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && (
          // role=alert: announced when it appears, so a screen-reader user is not
          // left wondering why the form did nothing.
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <Field>
          <FieldLabel>{t('login.identifier')}</FieldLabel>
          <FieldInput
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel>{t('login.password')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" className="w-full" loading={pending}>
          {t('login.submit')}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <Link to="/register" className="text-accent hover:underline">
            {t('login.no_account')}
          </Link>
          <Link to="/forgot-password" className="text-text-muted hover:underline">
            {t('password.forgot')}
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}
